import { useState } from "react";
import PolicyService from "../../../services/PolicyService/PolicyService.ts";
import type {
  BlankInventory,
  BlankSeries,
  LifecycleBlanks,
  ProviderId,
} from "../../../types/lifecycle.ts";
import { useLookup } from "./useLookup.ts";

export const splitBlank = (value: string): { series: string; serial: string } => {
  const raw = String(value ?? "").trim().toUpperCase().replace(/\s+/g, "");
  const match = /^([A-Z][A-Z0-9])(\d.*)$/.exec(raw);
  return match ? { series: match[1], serial: match[2] } : { series: "", serial: raw };
};

/** Group wing-backend's flat number list into the per-series shape the picker renders. */
export const toBlankSeries = (numbers: string[]): BlankSeries[] => {
  const bySeries = new Map<string, string[]>();
  for (const value of numbers) {
    if (!value) continue;
    const { series, serial } = splitBlank(value);
    if (!serial) continue;
    const list = bySeries.get(series) ?? [];
    list.push(serial);
    bySeries.set(series, list);
  }
  return [...bySeries.entries()].map(([series, available]) => ({
    series,
    label: series || "Без серия",
    providerCode: null,
    range: null,
    available,
    nextAvailable: available[0] ?? null,
  }));
};

const firstBlanks = (inventory?: BlankInventory): LifecycleBlanks => ({
  stickerSeries: inventory?.sticker[0]?.series ?? "",
  stickerNumber: inventory?.sticker[0]?.nextAvailable ?? "",
  greenCardSeries: inventory?.["green-card"][0]?.series ?? "",
  greenCardNumber: inventory?.["green-card"][0]?.nextAvailable ?? "",
});
export const paymentBlanks = (
  provider: ProviderId,
  blanks: LifecycleBlanks
): LifecycleBlanks | undefined => {
  if (provider === "dzi" || (!blanks.stickerNumber && !blanks.greenCardNumber))
    return undefined;
  return { ...blanks, territoryId: "5" };
};
export const validPaymentBlanks = (
  provider: ProviderId,
  blanks: LifecycleBlanks
): boolean => {
  if (provider === "levins")
    return (blanks.stickerNumber?.trim().length ?? 0) >= 6;
  if (
    provider === "generali" &&
    (blanks.stickerNumber || blanks.greenCardNumber)
  )
    return Boolean(
      blanks.stickerNumber?.trim() &&
        blanks.greenCardSeries?.trim() &&
        blanks.greenCardNumber?.trim()
    );
  return true;
};
/**
 * @param cid  the selected local INS_COMP.CID. Blanks are registered per
 *             insurer agency in wing-backend, so without it there is nothing
 *             to look up — the picker falls back to free-text entry.
 */
export function useBlankInventory(
  jwt: string,
  provider: ProviderId | "",
  revision: number,
  payment = false,
  cid = ""
) {
  const [refresh, setRefresh] = useState(0);
  const key =
    jwt && provider && cid && !(payment && provider === "dzi")
      ? `${jwt}|${provider}|${cid}|${revision}|${refresh}`
      : "";
  const lookup = useLookup(key, async (): Promise<BlankInventory> => {
    const [stickers, greenCards] = await Promise.all([
      PolicyService.fetchStickersByCompanyId(cid, jwt),
      PolicyService.fetchGreenCardsByCompanyId(cid, jwt),
    ]);
    return {
      provider: provider as ProviderId,
      sticker: toBlankSeries(stickers ?? []),
      "green-card": toBlankSeries(greenCards ?? []),
    };
  });
  const [chosen, setChosen] = useState<{ key: string; value: LifecycleBlanks }>(
    { key: "", value: {} }
  );
  const blanks =
    chosen.key === key
      ? chosen.value
      : payment && provider !== "levins"
      ? {}
      : firstBlanks(lookup.value);
  return {
    ...lookup,
    inventory: lookup.value ?? null,
    blanks,
    setBlanks: (value: LifecycleBlanks) => setChosen({ key, value }),
    refresh: () => setRefresh((value) => value + 1),
  };
}
